import { ref, type Ref } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { useTranslation } from "i18next-vue";
import APIClient from "@/APIClient";
import { QueryKey } from "@/stores/queries/query-keys";
import { useAIGuardError } from "@/composable/useAIGuardError";

/** Ce qu'il faut d'une observation pour la classer. */
export interface PartAssignable {
  id: number;
  partId?: number | null;
}

export interface PartAssigner {
  /**
   * Partie d'une observation : celle que l'inspecteur vient de choisir tant
   * que le serveur ne l'a pas confirmée, sinon celle rendue par le serveur.
   */
  partIdOf: (observation: PartAssignable) => number | null;
  /** Rattache à une partie (null = sans partie), enregistré aussitôt. */
  assign: (observation: PartAssignable, partId: number | null) => void;
  /** Rattache, ou détache si l'observation y était déjà (tap sur un badge). */
  toggle: (observation: PartAssignable, partId: number) => void;
}

/**
 * Classement des observations par partie, enregistré en ligne à chaque geste
 * sans faire attendre l'inspecteur : son choix s'affiche tout de suite, le
 * serveur suit.
 *
 * - Deux gestes sur la même observation partent dans l'ordre (le dernier
 *   gagne) ; des observations différentes s'enregistrent en parallèle.
 * - Tant qu'un enregistrement est en cours, le choix de l'inspecteur prime
 *   sur la liste du serveur : une relecture arrivée entre deux requêtes ne
 *   fait pas revenir un badge en arrière.
 * - Un enregistrement refusé rend la main à la valeur du serveur ; il est
 *   signalé une seule fois par rafale de gestes (une zone tracée lance
 *   autant de requêtes que d'observations).
 * - La liste n'est relue qu'une fois, quand tout est parti.
 */
export function createPartAssigner(options: {
  save: (observationId: number, partId: number | null) => Promise<unknown>;
  refresh: () => Promise<unknown>;
  onError?: (error: unknown) => void;
}): PartAssigner {
  /** Choix pas encore confirmés par la liste du serveur. */
  const wished = ref(new Map<number, number | null>());
  /** Dernier geste par observation — pour ne pas annuler un choix plus récent. */
  const latest = new Map<number, symbol>();
  /** File d'enregistrement par observation. */
  const queues = new Map<number, Promise<void>>();
  let pending = 0;
  let failureReported = false;

  const partIdOf = (observation: PartAssignable): number | null =>
    wished.value.has(observation.id)
      ? (wished.value.get(observation.id) ?? null)
      : (observation.partId ?? null);

  const dropWish = (observationId: number): void => {
    const next = new Map(wished.value);
    next.delete(observationId);
    wished.value = next;
  };

  /** Tout est parti : la liste du serveur redevient la seule référence. */
  const settle = async (): Promise<void> => {
    pending--;
    if (pending > 0) return;
    await options.refresh().catch(() => undefined);
    // Un geste arrivé pendant la relecture garde ses choix en attente.
    if (pending > 0) return;
    wished.value = new Map();
    latest.clear();
    queues.clear();
    failureReported = false;
  };

  const assign = (observation: PartAssignable, partId: number | null): void => {
    if (partIdOf(observation) === partId) return;
    const id = observation.id;
    const gesture = Symbol("part-assign");
    latest.set(id, gesture);
    wished.value = new Map(wished.value).set(id, partId);
    pending++;
    const run = (queues.get(id) ?? Promise.resolve())
      .then(() => options.save(id, partId))
      .then(
        () => undefined,
        (error: unknown) => {
          if (latest.get(id) === gesture) dropWish(id);
          if (failureReported) return;
          failureReported = true;
          options.onError?.(error);
        },
      )
      .then(settle);
    queues.set(id, run);
  };

  const toggle = (observation: PartAssignable, partId: number): void => {
    assign(observation, partIdOf(observation) === partId ? null : partId);
  };

  return { partIdOf, assign, toggle };
}

/**
 * Classement par partie des observations d'un ouvrage : chaque geste est
 * enregistré sur l'observation, en ligne (cf. `createPartAssigner`).
 *
 * L'appel passe par l'API directement, pas par la mutation partagée : celle-ci
 * relit la liste à chaque réponse (une zone tracée = autant de relectures) et,
 * hors connexion, met l'envoi en attente sans rien dire — le badge resterait
 * vert comme si c'était enregistré. Ici un envoi impossible échoue tout de
 * suite : le badge revient en arrière et l'inspecteur est prévenu.
 *
 * @param onFailure appelé quand un enregistrement est refusé (une fois par
 * rafale), après la notification — pour revérifier la connexion, par exemple.
 */
export function useObservationPartAssign(
  diagnosticId: Ref<number>,
  onFailure?: () => void,
): PartAssigner {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { createErrorHandler } = useAIGuardError();
  const notifyError = createErrorHandler(
    t("toast:fieldObservations.updateError"),
  );

  return createPartAssigner({
    save: (id, partId) => APIClient.updateFieldObservation(id, { partId }),
    // Liste et fiches des observations : tout ce qui porte leur partie.
    refresh: () =>
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_OBSERVATIONS],
      }),
    onError: (error) => {
      notifyError(error);
      // La partie visée a peut-être été supprimée ailleurs : l'arbre relu,
      // l'onglet met fin de lui-même au mode association.
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_PARTS, { diagnosticId: diagnosticId.value }],
      });
      onFailure?.();
    },
  });
}
